import Autocomplete from "@/components/Autocomplete";
import AvatarDetails from "@/components/AvatarDetails";
import Button from "@/components/Button";
import useModal from "@/hooks/useModal";
import { useSearch } from "@/hooks/useSearch";
import { Person } from "@/types";
import { useLazyQuery, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { useForm } from "react-hook-form";
import { EVENT_ELIGIBILITY_QUERY, FINALIZE_SEMINAR_REGISTRATIONS_MUTATION } from "../../../queries";
import { showSnackbar } from "@/lib/snackbarUtils";
import { useMutation } from "@apollo/client";
import { SOME_PEOPLE_QUERY } from "@/pages/people/queries";
import { capitalize } from "lodash";
import Banner from "@/components/Banner";
import { ApolloError } from "@apollo/client";

const defaultValues = {
  person: ""
};

type FieldValues = typeof defaultValues;

export default function OverrideRegistrationModal({
  onDismiss,
  eventId,
  universityLegacyId,
  refetch
}: {
  onDismiss: () => void;
  eventId: string;
  universityLegacyId?: string;
  refetch?: () => void;
}) {
  const isUniversityContext = Boolean(universityLegacyId);
  const headline = isUniversityContext ? "Register Student" : "Override Registration";
  const actionLabel = isUniversityContext ? "Finalize Registration" : "Register Person";
  const searchField = "person";
  const [pgaId, setPgaId] = useState("");
  const [bannerError, setBannerError] = useState("");
  const { searchValue, control, reset, setValue } = useSearch(searchField);
  const [variables, setVariables] = useState({});
  const { Modal } = useModal();
  const { data } = useQuery(SOME_PEOPLE_QUERY, { variables });
  const [fetchEventEligibility, { data: eligibilityData }] = useLazyQuery(EVENT_ELIGIBILITY_QUERY, {
    fetchPolicy: "network-only"
  });

  const eligibility = eligibilityData?.eventEligibility;
  const isIneligible = eligibility?.isEligible === false;

  const { handleSubmit } = useForm<FieldValues>({
    defaultValues,
    mode: "onChange"
  });

  function selectPayor(name: string, value: Person, setValue: (name: string, value: string) => void) {
    setPgaId(value.pgaId || "");
    setValue(name, `${value.firstName} ${value.lastName}`);

    if (value.pgaId) {
      fetchEventEligibility({ variables: { eventId, pgaId: value.pgaId } });
    }
  }

  useEffect(() => {
    setVariables(() => ({
      ...(searchValue ? { input: { search: searchValue } } : {})
    }));
  }, [searchValue]);

  const [finalizeSeminarRegistrations, { loading: mutationLoading }] = useMutation(
    FINALIZE_SEMINAR_REGISTRATIONS_MUTATION
  );

  const handleAddRegistrant = handleSubmit(async () => {
    if (!pgaId) {
      setBannerError("Select a person before finalizing the registration.");
      return;
    }

    try {
      const { data } = await finalizeSeminarRegistrations({
        variables: {
          input: {
            eventId,
            pgaId,
            ...(universityLegacyId ? { universityLegacyId } : {})
          }
        }
      });
      const payload = data?.finalizeSeminarRegistrations;
      if (payload?.success) {
        refetch?.();
        showSnackbar(payload.message ?? "Registrations finalized", { close: true });
        onDismiss();
      } else {
        setBannerError(payload?.message ?? "Could not finalize registrations.");
      }
    } catch (e) {
      setBannerError(`Error: ${(e as ApolloError).message}`);
    }
  });

  return (
    <Modal
      onClosed={onDismiss}
      action={
        <Button onClick={handleAddRegistrant} disabled={mutationLoading || !pgaId}>
          {actionLabel}
        </Button>
      }
      headline={headline}
      displayOverflow
    >
      <div className="md:w-[375px] space-y-6">
        <p className="text-body-medium text-sys-brand-on-surface">
          Select a person to register to this event. Eligibility rules will be ignored. Any required payments will be
          bypassed.
        </p>

        {!!bannerError && (
          <div className="mb-6">
            <Banner inline variant="error" description="The registrant could not be added." subText={bannerError} />
          </div>
        )}
        <Controller
          name="person"
          control={control}
          render={({ field: { onChange, value, name } }) => (
            <Autocomplete
              label="Person"
              required
              query={value}
              data={data?.people?.nodes || []}
              onChangeText={onChange}
              reset={() => {
                reset();
                setPgaId("");
              }}
              onSelect={(value) => selectPayor(name, value, setValue)}
              renderItem={(data: Person) => (
                <AvatarDetails
                  title={`${data.firstName} ${data.lastName}`}
                  text={
                    <div className="flex flex-col">
                      <div className="text-sys-brand-on-surface-variant">{data.pgaId}</div>
                      {data.latestProgram && (
                        <div className="text-sys-brand-on-surface-variant">
                          {data.latestProgram?.programType.name!} - {capitalize(data.latestProgram?.status)}
                        </div>
                      )}
                    </div>
                  }
                  picture={data.profilePhoto}
                  secondaryText={data.primarySectionAffiliation?.section?.name || ""}
                />
              )}
            />
          )}
        />
        {isIneligible && searchValue && (
          <div className="mb-6">
            <Banner inline variant="warn" description="" subText={eligibility?.eligibilityErrors || undefined} />
          </div>
        )}
      </div>
    </Modal>
  );
}
